<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <h1 class="title">Day trip groups</h1>
      <app-cells>
        <app-input
          @input.native="onGroupsSearch"
          v-model="search"
          class="input--200"
          type="search"
          placeholder="Search by group name"
          name="search"
        />
        <template v-if="is_sales_general_manager">
          <app-date-picker
            @change="onChangeFilter"
            v-model="filter_from"
            placeholder="From"
            class="datepicker--filter"
          />
          <app-date-picker
            @change="onChangeFilter"
            v-model="filter_to"
            placeholder="To"
            class="datepicker--filter"
          />
          <app-select
            @input="onChangeCategory"
            v-model="category_filter"
            :options="category_options"
            :reduce="item => item.id"
            :filterable="false"
            :searchable="false"
            placeholder="Category"
            class="select--200"
          />
          <app-filters
            v-model="range_type_filter"
            :filters="range_type_filters"
            name="filterRange"
            @change="onChangeFilter"
          />
        </template>
      </app-cells>
      <div v-for="trips_table_item of trips_table" :key="trips_table_item.name" class="table-block">
        <app-cells v-if="trips_table_item.name">
          <h2 class="title">{{ trips_table_item.name }}</h2>
        </app-cells>
        <v-client-table
            :data="trips_table_item.items"
            :columns="table.columns"
            :options="table.options"
            class="table-default table-default--climbing-groups-list"
            :class="{ 'table-default--loading': table_preloader }"
        >
          <template #h__route.name>
            <div class="table-default__left">Route</div>
          </template>
          <template #h__name>
            <div class="table-default__left">Name</div>
          </template>
          <template #name="{ row }">
            <div class="table-default__left">
              <router-link
                  :to="{ name: 'day-trips-groups-detail', params: { id: row.id } }"
                  class="table-default__link-border"
              >
                {{ row.name }}
              </router-link>
            </div>
          </template>
          <template #number_of_clients="{ row }">
            <span
                v-tooltip="{ content: clientTableList(row.clients), placement: 'top-start', offset: 10, html: true }"
                :style="{cursor: 'pointer'}"
            >
              {{ row.clients.length }}
            </span>
          </template>
          <template #date="{ row }">
            {{ dateFormat(row.date) }}
          </template>
          <template #pick_up_time="{ row }">
            <template v-if="!row.pick_up_time_to_be_confirmed">{{ row.pick_up_time }}</template>
            <span
              v-else
              v-tooltip="{ content: 'To be confirmed', placement: 'top-start', offset: 10, container: false }"
              class="status-tag"
              style="background-color: #f0c427"
            >
              {{ row.pick_up_time }}
            </span>
          </template>
          <template #pick_up_location="{ row }">
            <span v-tooltip="{ content: row.pick_up_location?.name, placement: 'top-start', offset: 10, container: false }">
              {{ row.pick_up_location?.short_name }}
            </span>
          </template>
          <template #activity="{ row }">{{ row.activity?.short_name }}</template>
          <template #ready_to_join="{ row }">
            <span
              class="status-tag"
              :style="{ backgroundColor: row.ready_to_join ? '#77b017' : '#000' }"
            >
              {{ row.ready_to_join ? 'Yes': 'No' }}
            </span>
          </template>
          <template #created_by="{ row }">
            <div class="table-default__controls">
              <div class="table-default__photo">
                <img
                    :src="getUserAvatar(row.created_by.crop.crop_url)" alt=""
                    v-tooltip="{ content: row.created_by.full_name, placement: 'top-start', offset: 10 }"
                >
              </div>
            </div>
          </template>
        </v-client-table>
      </div>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table_length" :page="pagination.page" :limit="40" />
      </app-cells>
    </template>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import { getDayTripsDayTripsByDated } from '@/http'
import { format, parse } from 'date-fns'

export default {
  name: 'DayTripsGroupsList',
  data() {
    return {
      is_loaded: false,
      table_preloader: false,
      search: '',
      filter_from: null,
      filter_to: null,
      trips_table: [],
      table_length: 0,
      table: {
        items: [],
        columns: ['name', 'number_of_clients', 'date', 'pick_up_time', 'pick_up_location', 'activity', 'ready_to_join', 'created_by'],
        options: {
          headings: {
            'name': 'Name',
            'number_of_clients': 'Pax',
            'date': 'Date',
            'pick_up_time': 'Pick-up time',
            'pick_up_location': 'Pick-up location',
            'activity': 'Activity',
            'ready_to_join': 'Join',
            'created_by': 'Manager',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      category_filter: 'upcoming',
      category_options: [
        {
          id: 'upcoming',
          name: 'Upcoming',
        },
        {
          id: 'next_two_weeks',
          name: 'Next 14 days',
        },
        {
          id: 'past',
          name: 'Past',
        },
      ],
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      range_type_filter: 'day',
      range_type_filters: [
        { value: 'month', label: 'Month' },
        { value: 'day', label: 'Day' },
      ],
    }
  },
  computed: {
    is_sales_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager') ||
        this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    fetchItems() {
      const params = {
        page: this.pagination.page,
        page_size: 40,
        search: this.search,
        category: this.search ? '' : this.category_filter,
        group_by: 'date',
        range_type: this.range_type_filter,
      }
      if (!this.search && this.filter_from) params['date__gte'] = this.filter_from
      if (!this.search && this.filter_to) params['date__lte'] = this.filter_to
      getDayTripsDayTripsByDated(params)
        .then(res => {
          this.trips_table = res.data.results.length ? res.data.results : [{name: '', items: []}]
          this.pagination.pages = res.data.pages
          this.pagination.count = res.data.count
          this.table_length = 0
          res.data.results.forEach(item => {
            this.table_length += item.items.length
          })
          this.is_loaded = true
          this.table_preloader = false
        })
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    onChangeCategory() {
      this.search = ''
      this.pagination.page = 1
      this.fetchItems()
    },
    onChangeFilter() {
      this.search = ''
      this.pagination.page = 1
      this.fetchItems()
    },
    onGroupsSearch() {
      this.searchGroups(this)
    },
    searchGroups: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
    clientTableList(clients) {
      let result = ''
      for (const key in clients) {
        result += `${clients[key].first_name} ${clients[key].last_name}<br>`
      }
      return result
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM') : ''
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
  }
}
</script>

<style lang="sass" scoped>
.table-block
  margin-bottom: 30px
</style>
